if (!customElements.get('product-360-spin')) {
  let runtimePromise = null;

  /**
   * The vendor runtime builds asset URLs as `pathModels + filename` and falls back to "/models" for an
   * empty path, so absolute Shopify URLs are split into a protocol base and a host-relative remainder.
   */
  const splitUrl = (url) => {
    const parsed = new URL(url, window.location.href);
    return {
      base: `${parsed.protocol}//`,
      path: `${parsed.host}${parsed.pathname}${parsed.search}`,
    };
  };

  const normalizeColor = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '');

  /**
   * Metaobject labels may carry an admin-friendly prefix ("iPhone 18 Pro Max – Black"). The viewer shows
   * and matches only the color name after the last spaced dash ("Black").
   */
  const toColorName = (label) => {
    const parts = String(label || '').split(/\s+[–—-]\s+/);
    return parts[parts.length - 1].trim();
  };

  class Product360Spin extends HTMLElement {
    constructor() {
      super();
      this.config = JSON.parse(this.querySelector('[data-360-spin-config]').textContent);
      this.panel = this.querySelector('.product-360-spin__panel');
      this.closeButton = this.querySelector('.product-360-spin__close');
      this.initialized = false;
      this.lastSelectedKey = '';
      this.opener = null;

      // The vendor builds swatch classes/ids from `label` (replacing only its first space) and picks the
      // selected color with `label.includes(selected)`, so it receives a unique space-free key as `label`.
      this.models = this.config.models.map((model, index) => {
        const name = toColorName(model.label);
        return { ...model, index, name, key: `m${index}-${normalizeColor(name) || 'color'}` };
      });

      this.onKeyUp = this.onKeyUp.bind(this);
      this.onViewerEvent = this.onViewerEvent.bind(this);
      this.querySelectorAll('[data-360-spin-close]').forEach((element) =>
        element.addEventListener('click', this.close.bind(this))
      );
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      // Keep a single instance on <body> so theme editor section reloads do not stack modals.
      document.querySelectorAll(`body > product-360-spin#${CSS.escape(this.id)}`).forEach((element) => {
        if (element !== this) element.remove();
      });
      document.body.appendChild(this);
      window.addEventListener('3dpv:event', this.onViewerEvent);
    }

    open(opener) {
      this.opener = opener;
      const selectedModel = this.getSelectedModel();
      const selectedKey = selectedModel ? selectedModel.key : '';
      document.getElementById(this.config.entryId).dataset.selectedColor = selectedKey;

      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keyup', this.onKeyUp);
      trapFocus(this.panel, this.closeButton);
      this.publish('pdp_360_view_open', { selected_color: selectedModel ? selectedModel.name : '' });

      this.loadRuntime()
        .then(() => {
          window.requestAnimationFrame(() => {
            if (!this.initialized) {
              this.initViewer();
            } else if (selectedKey && selectedKey !== this.lastSelectedKey) {
              window.dispatchEvent(
                new CustomEvent('channel-html:3djs:changeDefaultSelected', { detail: { selectedColor: selectedKey } })
              );
            }
            if (selectedKey) this.lastSelectedKey = selectedKey;
          });
        })
        .catch((error) => console.error('Product360Spin: failed to load viewer runtime', error));
    }

    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keyup', this.onKeyUp);
      removeTrapFocus(this.opener && document.body.contains(this.opener) ? this.opener : null);
    }

    onKeyUp(event) {
      if (event.code === 'Escape') this.close();
    }

    loadRuntime() {
      if (!runtimePromise) {
        runtimePromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = this.config.runtimeUrl;
          script.onload = resolve;
          script.onerror = (error) => {
            runtimePromise = null;
            script.remove();
            reject(error);
          };
          document.head.appendChild(script);
        });
      }
      return runtimePromise;
    }

    initViewer() {
      const models = this.models.map((model) => ({ ...model, file: splitUrl(model.url) }));
      const ibl = splitUrl(this.config.iblUrl);

      // Replaces the demo config baked into the end of apple360spin.js.
      window.__CHANNEL_HTML_3D_CONFIG__ = {
        className: '',
        axDeviceName: this.config.deviceName,
        pathTextures: ibl.base,
        pathModels: models[0].file.base,
        containerClass: this.config.containerClass,
        svgPath: this.config.svgUrl,
        modalData: {
          isModal: false,
          customModalEntryID: this.config.entryId,
          defaultModalEntryText: '',
        },
        modelFileData: models.map((model) => ({
          label: model.key,
          labelName: model.name,
          color: model.color,
          filename: model.file.path,
          index: model.index,
          alignmentOffset: 0.8,
        })),
        iblFilename: ibl.path,
        actionPromptDesktop: this.config.promptDesktop,
        actionPromptMobile: this.config.promptMobile,
        idleResetTime: 10,
        toneMapping: 'Linear',
        exposure: 1,
        cameraPosition: { x: 0, y: 0, z: 30 },
        minDistance: 30,
        maxDistance: 30,
        autoRotate: false,
        autoRotateSpeed: -5,
        dampingFactor: 0.35,
        keyControlDistance: 15,
      };

      window.dispatchEvent(new CustomEvent('channel-html:3djs:init'));
      this.initialized = true;
    }

    /**
     * Resolves the PDP's currently selected color (checked color/case finish radio, see main-product
     * variant picker) to the matching model, or null when no model matches.
     */
    getSelectedModel() {
      const colorOptionNames = this.config.colorOptionNames.filter(Boolean);
      const checkedInput = Array.from(document.querySelectorAll('input.smart_radio:checked')).find((input) =>
        colorOptionNames.includes(input.dataset.label_name)
      );
      const colorName = document.querySelector('.color-name');
      const selectedColor = normalizeColor(
        checkedInput ? checkedInput.value : colorName ? colorName.textContent.replace(/^\s*-\s*/, '') : ''
      );
      if (!selectedColor) return null;

      return (
        this.models.find((model) => normalizeColor(model.name) === selectedColor) ||
        this.models.find((model) => normalizeColor(model.label) === selectedColor) ||
        null
      );
    }

    onViewerEvent(event) {
      const key = event.detail && event.detail.key;
      const prefix = 'click:swatch:';
      if (this.hidden || !key || key.indexOf(prefix) !== 0) return;
      const model = this.models.find((item) => item.key === key.slice(prefix.length));
      this.publish('pdp_360_view_swatch', { selected_color: model ? model.name : key.slice(prefix.length) });
    }

    publish(eventName, data) {
      if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
        return;
      }
      window.Shopify.analytics.publish('APLdigitalDataEvent', {
        event: eventName,
        product_title: this.config.deviceName,
        ...data,
      });
    }
  }

  customElements.define('product-360-spin', Product360Spin);

  // Delegated so triggers survive the AJAX media gallery swap on color change.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-360-spin-open]');
    if (!trigger) return;
    const viewer = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!viewer || typeof viewer.open !== 'function') return;
    event.preventDefault();
    viewer.open(trigger);
  });
}
